<template>
  <div class="bg-lightGrey">
    <div class="bg-whiteOverride rounded border-border border-1 border-solid">

      <div class="p-lg">
        <h1>Cards</h1>

        <div class="pb-lg">
          <h2>&lt;CardContainer&gt;</h2>
          <CardContainer class="p-sm">
            <p>Dummy Content</p>
          </CardContainer>
        </div>

        <hr />

        <div class="pb-lg">
          <h2>&lt;HeaderCard&gt;</h2>
          <HeaderCard>
            <template v-slot:header>
              Header here
            </template>
            <template v-slot:content>
              <div class="p-sm">
                <p>Dummy Content</p>
              </div>
            </template>
          </HeaderCard>
        </div>

        <hr />

        <div class="pb-lg">
          <h2>&lt;ExpandableCard&gt; (#1 - default controls)</h2>
          <p class="italic text-primaryGrey">See console for amplitude events (event category: card1)</p>
          <ExpandableCard
            minimiseLabel="Minimise"
            maximiseLabel="Maximise"
            logEventCategory="card1"
            @minimised="() => cardMinimised(1)"
            @maximised="() => cardMaximised(1)"
            @toggled="(newVal) => cardToggled(1, newVal)"
          >
            <template v-slot:header>
              Header here
            </template>
            <template v-slot:content>
              <div class="p-sm">
                <p>Dummy Content</p>
              </div>
            </template>
            <template v-slot:expandedContent>
              <div class="p-sm">
                <p>Expanded Content</p>
              </div>
            </template>
          </ExpandableCard>
        </div>

        <hr />

        <div class="pb-lg">
          <h2>&lt;ExpandableCard&gt; (#2 - toggle controls)</h2>
          <p class="italic text-primaryGrey">See console for amplitude events (event category: card2)</p>
          <ExpandableCard
            toggleable
            minimiseLabel="Minimise"
            maximiseLabel="Maximise"
            logEventCategory="card2"
            @minimised="() => cardMinimised(2)"
            @maximised="() => cardMaximised(2)"
            @toggled="(newVal) => cardToggled(2, newVal)"
          >
            <template v-slot:header>
              Header here
            </template>
            <template v-slot:content>
              <div class="p-sm">
                <p>Dummy Content</p>
              </div>
            </template>
            <template v-slot:expandedContent>
              <div class="p-sm">
                <p>Expanded Content</p>
              </div>
            </template>
          </ExpandableCard>
        </div>

        <hr />

        <div class="pb-lg">
          <h2>&lt;ExpandableCard&gt; (#3 - custom controls)</h2>
          <p class="italic text-primaryGrey">See console for amplitude events (event category: card3, specified/overridden event types)</p>
          <ExpandableCard
            minimiseLabel="Minimise"
            maximiseLabel="Maximise"
            logEventCategory="card3"
            logMinimiseEventName="minimised_card_3"
            logMaximiseEventName="maximised_card_3"
            logToggleEventName="toggled_card_3"
            @minimised="() => cardMinimised(3)"
            @maximised="() => cardMaximised(3)"
            @toggled="(newVal) => cardToggled(3, newVal)"
          >
            <template v-slot:header>
              Header here
            </template>
            <template v-slot:controls>
              No controls here
            </template>
            <template v-slot:content="{ minimise, maximise, toggle, isExpanded }">
              <div class="p-sm">
                <p>Dummy Content</p>
                <p>Current status: {{ isExpanded ? 'Expanded' : 'Collapsed' }} </p>
                <p>
                  <span
                    @click="minimise"
                    class="cursor-pointer underline"
                  >
                    Minimise
                  </span>,
                  <span
                    @click="maximise"
                    class="cursor-pointer underline"
                  >
                    Maximise
                  </span>
                  or
                  <span
                    @click="toggle"
                    class="cursor-pointer underline"
                  >
                    Toggle
                  </span>.
                </p>
              </div>
            </template>
            <template v-slot:expandedContent>
              <div class="p-sm">
                <p>Expanded Content</p>
              </div>
            </template>
          </ExpandableCard>
        </div>

        <hr />

        <div class="pb-lg">
          <h2>&lt;PartyRoleCard&gt;</h2>
          <p class="italic text-primaryGrey">See console for amplitude events (event category: party_role)</p>
          <p class="flex justify-end">
            <BaseButton
              v-if="hasACardBeenRemoved"
              variant="tertiary"
              size="small"
              @click.native="resetCards"
              class="mr-base"
            >
              Reset Cards
            </BaseButton>
            <BaseButton
              variant="tertiary"
              size="small"
              @click.native="toggleAllPartyRoleCards">
              <span class="flex justify-center items-center">
                <span>
                  {{ areAllPartyRoleCardsExpanded ? 'Collapse All' : 'Expand All' }}
                </span>
                <span class="inline-block ml-sm">
                  <MinimiseControlIcon
                    v-show="areAllPartyRoleCardsExpanded"
                    class="w-{20px}"
                  />
                  <MaximiseControlIcon
                    v-show="!areAllPartyRoleCardsExpanded"
                    class="w-{20px}"
                  />
                </span>
              </span>
            </BaseButton>
          </p>
          <PartyRoleCard
            v-for="item of activeParties"
            :key="item.partyName"
            :partyName="item.partyName"
            :onlyCopy="item.onlyCopy"
            :expanded="item.expanded"
            logEventCategory="party_role"
            @minimised="() => minimiseCard(item)"
            @maximised="() => maximiseCard(item)"
            @removed="() => removeCard(item)"
            @addedMyselfAsParty="() => addMyselfAsAPartyTo(item)"
            @receivedOnlyACopy="(value) => receiveOnlyCopyFor(item, value)"
            class="mb-base"
          >
            <template v-slot:overview>
              <p class="p-sm"><b>Overview:</b></p>
              <p class="p-sm">Added myself as a party? {{ item.addedMyself ? 'Yes' : 'No' }}</p>
              <p class="p-sm">Receiving only a copy? {{ item.onlyCopy ? 'Yes' : 'No' }}</p>
            </template>
            <template v-slot:form>
              <div class="p-sm">
                <svg width="400" height="200" viewBox="0 0 400 200" fill="none">
                  <g>
                    <rect x="0" y="0" width="100%" height="100%" fill="#F5F5F5" stroke="#D9D9D9" stroke-alignment="inside"/>
                    <line x1="0" y1="0" x2="100%" y2="100%" stroke="#D9D9D9"/>
                    <line x1="0" y1="100%" x2="100%" y2="0" stroke="#D9D9D9"/>
                  </g>
                  <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#D9D9D9" style="font: bold 30px sans-serif;">FORM</text>
                </svg>
              </div>
            </template>
            <template v-slot:templateSelector>
              <div class="p-sm">
                <svg width="400" height="200" viewBox="0 0 400 200" fill="none">
                  <g>
                    <rect x="0" y="0" width="100%" height="100%" fill="#F5F5F5" stroke="#D9D9D9" stroke-alignment="inside"/>
                    <line x1="0" y1="0" x2="100%" y2="100%" stroke="#D9D9D9"/>
                    <line x1="0" y1="100%" x2="100%" y2="0" stroke="#D9D9D9"/>
                  </g>
                  <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#D9D9D9" style="font: bold 30px sans-serif;">TEMPLATE SELECTOR</text>
                </svg>
              </div>
            </template>
          </PartyRoleCard>
        </div>

      </div>

    </div>
  </div>
</template>

<script setup>
/* eslint-disable no-param-reassign */
import { ref, computed, watchEffect } from 'vue';
import { CardContainer } from '@/components/library/containers';
import { HeaderCard, ExpandableCard } from '@/components/library/cards';
import { PartyRoleCard } from '@/components/ui/envelopes/cards';
import { MinimiseControlIcon, MaximiseControlIcon } from '@/components/icons';
import BaseButton from '@/components/library/buttons/BaseButton.vue';

function cardMinimised(n) {
  console.log(`Card ${n}: minimised`);
}

function cardMaximised(n) {
  console.log(`Card ${n}: maximised`);
}

function cardToggled(n, newVal) {
  console.log(`Card ${n}: toggled; new value: ${newVal}`);
}

const parties = ref([
  {
    partyName: 'Party 1',
    expanded: false,
    addedMyself: false,
    onlyCopy: false,
    removed: false,
  },
  {
    partyName: 'Party 2',
    expanded: false,
    addedMyself: false,
    onlyCopy: false,
    removed: false,
  },
  {
    partyName: 'Party 3',
    expanded: false,
    addedMyself: false,
    onlyCopy: false,
    removed: false,
  },
  {
    partyName: 'Party 4',
    expanded: false,
    addedMyself: false,
    onlyCopy: false,
    removed: false,
  },
  {
    partyName: 'Party 5',
    expanded: false,
    addedMyself: false,
    onlyCopy: false,
    removed: false,
  },
  {
    partyName: 'Party 6',
    expanded: false,
    addedMyself: false,
    onlyCopy: false,
    removed: false,
  },
]);

const areAllPartyRoleCardsExpanded = ref(false);
const hasACardBeenRemoved = ref(false);
const activeParties = computed(() => parties.value.filter(item => !item.removed));

watchEffect(() => {
  areAllPartyRoleCardsExpanded.value = parties.value.every(item => item.expanded);
  console.log('Parties:', parties.value);
});

function toggleAllPartyRoleCards() {
  areAllPartyRoleCardsExpanded.value = !areAllPartyRoleCardsExpanded.value;
  parties.value.forEach((item) => {
    item.expanded = areAllPartyRoleCardsExpanded.value;
  });
}

function minimiseCard(item) {
  item.expanded = false;
  cardMinimised(item.partyName);
}

function maximiseCard(item) {
  item.expanded = true;
  cardMaximised(item.partyName);
}

function removeCard(item) {
  item.removed = true;
  hasACardBeenRemoved.value = true;
  console.log(`Card ${item.partyName}: removed`);
}

function addMyselfAsAPartyTo(item) {
  item.addedMyself = true;
  console.log('Added myself to', item.partyName);
}

function receiveOnlyCopyFor(item, value) {
  item.onlyCopy = value;
  console.log('Receive only copy for', item.partyName);
}

function resetCards() {
  parties.value.forEach((party) => {
    party.expanded = false;
    party.addedMyself = false;
    party.onlyCopy = false;
    party.removed = false;
  });
  hasACardBeenRemoved.value = false;
  console.log('Cards reset');
}
</script>
